// OPS-01 — the complete, oldest-first paged work list. Unlike the websocket
// board's newest-300 snapshot, every page comes from /dispatch/work-list at
// offset zero onward. Rows remain legacy/unverified and grant no actions.
import { useEffect, useRef, useState } from "react";
import { RotateCcw } from "lucide-react";

import { ApiError, apiGet } from "../api";
import type { DeliveryCard } from "../types";

type Lane = "queue" | "active" | "attention";
interface Page {
  lane: Lane;
  items: DeliveryCard[];
  offset: number;
  limit: number;
  has_more: boolean;
  next_offset: number | null;
}

const LANES: { id: Lane; label: string }[] = [
  { id: "queue", label: "Queue" },
  { id: "active", label: "Active" },
  { id: "attention", label: "Attention" },
];

export default function WorkList() {
  const [lane, setLane] = useState<Lane>("queue");
  const [offset, setOffset] = useState(0);
  const [revision, setRevision] = useState(0);
  const [page, setPage] = useState<Page | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const sequence = useRef(0);

  useEffect(() => {
    const request = ++sequence.current;
    setLoading(true);
    setPage(null);
    apiGet<Page>(`/dispatch/work-list?lane=${lane}&offset=${offset}`)
      .then((value) => {
        if (sequence.current !== request) return;
        setPage(value);
        setErr(null);
      })
      .catch((e) => {
        if (sequence.current !== request) return;
        setErr(e instanceof ApiError ? e.message : "Could not load work list");
      })
      .finally(() => {
        if (sequence.current === request) setLoading(false);
      });
    return () => { ++sequence.current; };
  }, [lane, offset, revision]);

  return (
    <div className="board-page">
      <div className="board-header"><h1>Delivery work list</h1></div>
      <p className="cap-note">
        Current shop rows, oldest first, 50 at a time. Each page is a fresh read, not a frozen
        snapshot; if assignments change while paging, refresh from page one. Legacy statuses and
        assignments here are unverified observations and do not authorize dispatch or custody actions.
      </p>
      <div className="dlq-tabs">
        {LANES.map((item) => (
          <button key={item.id} className={`dlq-tab ${lane === item.id ? "active" : ""}`}
            onClick={() => { setLane(item.id); setOffset(0); }}>
            {item.label}
          </button>
        ))}
      </div>
      {err && <div className="banner-err" role="alert">{err}</div>}
      <section className="lane dlq-lane">
        <header className="lane-head">
          <span>{loading ? "Loading…" : `${page?.items.length ?? 0} shown · offset ${offset}`}</span>
          <button className="courier-btn" disabled={loading} onClick={() => { setOffset(0); setRevision((n) => n + 1); }}>
            <RotateCcw size={12} /> Refresh from first page
          </button>
        </header>
        <div className="lane-body">
          {page?.items.length === 0 && <p className="lane-empty">No rows on this page.</p>}
          {page?.items.map((item) => (
            <div className="dlq-row" key={item.id}>
              <span className="dlq-status">{item.status}</span>
              <div className="dlq-main">
                <span className="dlq-event">{item.order_name || item.id.slice(0, 8)}</span>
                <span className="dlq-sub">{item.city || "city unknown"} · legacy assignment: {item.driver_name || "unassigned"}</span>
              </div>
              <span className="cap-note">Unverified · no action</span>
            </div>
          ))}
        </div>
        <div className="recover-actions" style={{ padding: "8px 12px" }}>
          <button className="courier-btn" disabled={offset === 0 || loading} onClick={() => setOffset(Math.max(0, offset - (page?.limit ?? 50)))}>
            Previous
          </button>
          <button className="courier-btn" disabled={!page?.has_more || loading || page.next_offset === null}
            onClick={() => { if (page?.next_offset != null) setOffset(page.next_offset); }}>
            Next page
          </button>
        </div>
      </section>
    </div>
  );
}
